import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link } from '@tanstack/react-router';

import { JobDetailView } from './JobDetailView.js';
import { JobForm } from './JobForm.js';
import { useJobQuery } from './useJobQuery.js';
import { useJobsQuery } from './useJobsQuery.js';

/**
 * The `/jobs/$id` job-detail route. The static `new` segment renders the create form (the bare
 * `$id` route hosts both "new" and a numeric id — TanStack resolves the literal nowhere here, so
 * we branch on the param). For a numeric id it fires `GET /api/v1/jobs/:id` (the job + the
 * server-computed Profitability aggregate) and the coarse list (to source the merge candidates),
 * then renders the tabbed `JobDetailView`.
 *
 * Loading → accessible `Spinner`; error / not-found → announced error state with a back link.
 * The SPA never re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/jobs/$id');

export function JobDetailRoute() {
  const { id } = routeApi.useParams();

  // The create form (the api-host create endpoint stays SSR this plan — JobForm links back). This
  // branch is BEFORE any hook so the detail body's hooks always run in a stable order (a fresh
  // component mounts per branch — rules-of-hooks safe).
  if (id === 'new') {
    return <JobForm mode="create" />;
  }

  return <JobDetailLoader jobId={Number(id)} />;
}

function JobDetailLoader({ jobId }: { jobId: number }) {
  const { t } = useTranslation();
  const query = useJobQuery(jobId);
  // Source the merge candidates from the coarse list (already cached on the list view).
  const listQuery = useJobsQuery({});

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  const job = query.isSuccess ? query.data.job : undefined;
  const profitability = query.isSuccess ? query.data.profitability : undefined;

  if (query.isError || !job || !profitability) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('jobs:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('jobs:error.detailBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/jobs">{t('jobs:actions.backToJobs')}</Link>
        </Button>
      </section>
    );
  }

  return (
    <JobDetailView
      job={job}
      profitability={profitability}
      candidateJobs={listQuery.data?.jobs ?? []}
    />
  );
}
